/**
 * Branded QR generation — renders a QR in Parkify signage style: pure-black
 * square data modules, rounded finder "eyes" on the three corners, and the
 * purple stacked chevron centered. Matches the client-branded artwork operators
 * had been making by hand in an external QR styling tool.
 *
 * We render our own SVG from qrcode's module matrix because the stock SVG
 * renderer can only draw plain square finders — it can't round the eyes. All
 * geometry is in module units; the viewBox is `0 0 dim dim` where
 * dim = matrix size + 2·MARGIN.
 *
 * Scannability: error-correction level 'H' (~30% recovery); the center logo
 * clears ~16% of the code area (a box at 40% of the side — the largest that
 * still stays clear of the finders on the smallest QR versions we generate),
 * and rounding the eyes leaves the 1:1:3:1:1 finder ratio intact. Verified
 * end-to-end by rasterizing the output and decoding it with a real QR reader
 * across 9 URLs from the shortest to longest zone/validation links we
 * generate (short zone ids produce the fewest modules and are the tightest
 * fit against the finders).
 *
 * The mark is the real logo, not a redraw: the three ink <path>s are lifted
 * verbatim from parkify-logo.svg and a nested-<svg> viewBox crops to the mark's
 * measured bounding box, so the "Parkify" wordmark to its left is clipped away.
 *
 * Shared by the ops console and the tenant portal (`@parkify/ui/qr-brand`). It
 * was two byte-identical copies until 2026-10-22. The QR ENCODING stays with
 * each app (`qrcode` is the app's dependency, not this package's), so a second
 * copy of the encoder can never ride into a bundle: the app's
 * `generateBrandedQrSvg` encodes and hands the module matrix to
 * `renderBrandedQrSvg` here.
 */

// Parkify Master Purple — the stacked-chevron mark in the QR center.
const CHEVRON_COLOR = '#7F3F98'
// QR ink + paper. Pure black to match the client's branded signage style.
const QR_DARK = '#000000'
const QR_LIGHT = '#FFFFFF'

// Quiet-zone width, in modules (the QR spec recommends ≥ 4).
const MARGIN = 4
// Corner radius of the rounded finder "eyes", in module units. The eyes'
// footprint is fixed at 7×7 modules by the QR spec and can't be enlarged —
// tested empirically, even a 10% footprint increase corrupts the adjacent
// timing pattern and fails to decode on every QR size, since that structural
// feature isn't protected by error correction the way data modules are. The
// corner radius is the one safe lever for making the eyes read as "bigger":
// it doesn't touch the finder's dark/light structure. 3 (1.5x of the original
// 2) is close to the practical ceiling — 3.5 would make a 7-wide square a
// full circle, losing the "rounded square" look.
const EYE_RADIUS = 3
// Center logo box as a fraction of the module count. 0.40 is the largest value
// that still decodes reliably on the smallest realistic QR versions (short
// zone ids like "mg1" produce fewer modules, so the same fraction eats a
// bigger bite out of the code relative to the finder patterns) — verified by
// decoding across 9 URLs spanning the shortest to longest zone/validation
// links we generate. The chevron is drawn at CHEVRON_INSET of that box,
// leaving visible white padding on all sides rather than filling it.
const LOGO_FRAC = 0.40
const CHEVRON_INSET = 0.765

// The chevron mark's bounding box inside parkify-logo.svg (viewBox 0 0 129.21
// 30.85), measured from the rasterized logo: x=93 y=4.4 w=31.1 h=22.3.
const MARK_VIEWBOX = '93 4.4 31.1 22.3'

// The three ink <path> d-attributes of parkify-logo.svg, verbatim (wordmark +
// mark). MARK_VIEWBOX crops the nested svg to just the mark, so the wordmark is
// never visible. Recolored to CHEVRON_COLOR at render time.
const CHEVRON_PATHS: string[] = [
  "M18.37 16.17c0 3.78-2.62 6.16-5.85 6.16-1.39 0-2.51-.42-3.32-1.25v5.32H5.02V10.21H9v1.19c.79-.95 1.96-1.39 3.52-1.39 3.23 0 5.85 2.38 5.85 6.16Zm-4.22 0c0-1.8-1.1-2.84-2.51-2.84s-2.51 1.03-2.51 2.84 1.1 2.84 2.51 2.84 2.51-1.03 2.51-2.84Zm17.23-.66v6.62h-3.9v-1.54c-.59 1.14-1.78 1.74-3.52 1.74-2.86 0-4.44-1.61-4.44-3.67 0-2.2 1.61-3.56 5.3-3.56h2.38c-.07-1.21-.88-1.89-2.53-1.89-1.14 0-2.42.4-3.23 1.03l-1.41-2.84c1.36-.9 3.39-1.39 5.26-1.39 3.87 0 6.1 1.72 6.1 5.5ZM27.2 18.3v-1.03h-1.8c-1.34 0-1.83.48-1.83 1.21s.57 1.21 1.56 1.21c.92 0 1.74-.44 2.07-1.39Zm13.61-8.29v3.76c-.37-.04-.64-.07-.95-.07-1.69 0-2.86.86-2.86 2.95v5.48h-4.18V10.21h3.98v1.43c.86-1.08 2.24-1.63 4-1.63Zm6.84 7.94-1.23 1.25v2.93h-4.18V5.81h4.18v8.56l4.27-4.16h4.95l-4.95 5.1 5.37 6.82H51l-3.34-4.18Zm9.27-11.28c0-1.25 1.01-2.22 2.55-2.22s2.55.9 2.55 2.16c0 1.32-1.01 2.29-2.55 2.29s-2.55-.97-2.55-2.22Zm.46 3.54h4.18v11.93h-4.18V10.21Zm11.44.44h2.84v3.12h-2.73v8.36h-4.18v-8.36h-1.74v-3.12h1.74v-.31c0-2.86 1.78-4.73 4.97-4.73 1.01 0 2.09.2 2.77.62l-1.03 2.95c-.35-.2-.81-.35-1.25-.35-.88 0-1.39.51-1.39 1.56v.26Zm18.17-.44-5.15 12.41c-1.25 3.08-3.01 3.98-5.37 3.98-1.25 0-2.66-.42-3.45-1.08l1.45-2.93c.48.44 1.19.7 1.85.7.81 0 1.28-.31 1.63-1.01l-5.1-12.08h4.29l2.93 7.28 2.95-7.28H87Zm21.57-5.76-15.54 7.42 3.85 1.83 1.88.9 1.95.93 1.9.9 5.97 2.85 5.98-2.85 1.9-.9 1.95-.93 1.88-.9 3.85-1.83-15.55-7.42Zm0 13.03-4.08-1.95 4.08-1.95 4.09 1.95-4.09 1.95Zm5.98-2.85-5.98-2.85-5.97 2.85-1.96-.93 7.93-3.78 7.94 3.78-1.96.93Zm3.85-1.83-9.82-4.68-9.81 4.68-1.96-.93 11.77-5.61 11.77 5.61-1.95.93Z",
  "m122.22 18.29-1.88.9-1.96.93-1.89.9-7.93 3.78-7.92-3.78-1.89-.9-1.96-.93-1.88-.9-1.89.9.29.14 1.6.76 1.96.93 1.88.9 9.82 4.68 9.81-4.68 1.88-.9 1.96-.93 1.6-.76.29-.14-1.89-.9z",
  "m122.22 14.63-1.89.9-1.96.93-1.88.9-7.93 3.78-7.93-3.78-1.88-.9-1.96-.93-1.89-.9-1.88.9.29.14 1.59.76 1.96.93 1.89.9 9.81 4.68 9.81-4.68 1.89-.9 1.96-.93 1.59-.76.29-.14-1.88-.9z",
]

// A rounded finder "eye" (7×7 modules): black rounded ring, white gap, black
// rounded center dot — same 1:1:3:1:1 structure as a plain finder, just rounded.
function eye(fr: number, fc: number): string {
  const x = MARGIN + fc, y = MARGIN + fr
  return `<rect x="${x}" y="${y}" width="7" height="7" rx="${EYE_RADIUS}" fill="${QR_DARK}"/>` +
    `<rect x="${x + 1}" y="${y + 1}" width="5" height="5" rx="${(EYE_RADIUS - 0.7).toFixed(2)}" fill="${QR_LIGHT}"/>` +
    `<rect x="${x + 2}" y="${y + 2}" width="3" height="3" rx="${Math.max(0.6, EYE_RADIUS - 1.3).toFixed(2)}" fill="${QR_DARK}"/>`
}

/**
 * Render the branded QR as an SVG string from qrcode's module matrix. Skips the
 * three finder patterns (redrawn as rounded eyes) and a central box (cleared for
 * the chevron), draws every other dark module as a 1×1 rect.
 */
/** The module matrix qrcode's `create(url).modules` returns. */
export interface QrModuleMatrix {
  size: number
  data: Uint8Array
}

/**
 * Render an encoded QR (error-correction 'H', see the header) as the branded
 * SVG. Pure: no encoder, no DOM.
 */
export function renderBrandedQrSvg(matrix: QrModuleMatrix): string {
  const { size, data } = matrix
  const dim = size + MARGIN * 2
  const dark = (r: number, c: number) =>
    r >= 0 && c >= 0 && r < size && c < size && data[r * size + c] === 1

  // Finder patterns (7×7): top-left, top-right, bottom-left.
  const finders: Array<[number, number]> = [[0, 0], [0, size - 7], [size - 7, 0]]
  const inFinder = (r: number, c: number) =>
    finders.some(([fr, fc]) => r >= fr && r < fr + 7 && c >= fc && c < fc + 7)

  // Central clear box (module indices [lo, hi)) that the chevron sits in.
  const box = Math.round(size * LOGO_FRAC)
  const lo = Math.floor((size - box) / 2)
  const hi = lo + box
  const inLogo = (r: number, c: number) => r >= lo && r < hi && c >= lo && c < hi

  let rects = ''
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (!dark(r, c) || inFinder(r, c) || inLogo(r, c)) continue
      rects += `<rect x="${MARGIN + c}" y="${MARGIN + r}" width="1" height="1"/>`
    }
  }

  const eyes = finders.map(([fr, fc]) => eye(fr, fc)).join('')

  const center = dim / 2
  const mark = box * CHEVRON_INSET
  const xy = (center - mark / 2).toFixed(3)
  const chevron =
    `<svg x="${xy}" y="${xy}" width="${mark.toFixed(3)}" height="${mark.toFixed(3)}" ` +
    `viewBox="${MARK_VIEWBOX}" preserveAspectRatio="xMidYMid meet" overflow="hidden">` +
    CHEVRON_PATHS.map(d => `<path d="${d}" fill="${CHEVRON_COLOR}"/>`).join('') +
    `</svg>`

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dim} ${dim}" shape-rendering="crispEdges">` +
    `<rect width="100%" height="100%" fill="${QR_LIGHT}"/>` +
    `<g fill="${QR_DARK}">${rects}</g>${eyes}${chevron}</svg>`
}
